import { isGphFromSources } from '../automatic-distribution-employee';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-automatic-distribution-schedule-new',
  templateUrl: './automatic-distribution-schedule.component.html',
  styleUrls: ['./automatic-distribution-schedule.component.scss'],
  standalone: false,
})
export class AutomaticDistributionScheduleComponent implements AfterViewChecked, OnChanges {
  @Input() result;
  @Input() tickets;
  @Input() originalTickets;
  @Input() brigades;
  @Input() employees;
  @ViewChild('scheduleTable') scheduleTable: ElementRef;

  public moment = require('moment');
  public TICKETS_PATHS = TICKETS_PATHS;
  public showFullTimeline = false;
  public readonly timelineHourWidth = 140;
  public readonly visibleHoursCount = 7;
  private plannedOrdersLayouts = {};
  private routesReference = null;
  private shouldSetInitialScroll = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.result) {
      const nextRoutesReference = this.getRoutes(this.result);

      if (nextRoutesReference !== this.routesReference) {
        this.routesReference = nextRoutesReference;
        this.plannedOrdersLayouts = this.makePlannedOrdersLayouts(nextRoutesReference);
        this.shouldSetInitialScroll = true;
      }
    }
  }

  ngAfterViewChecked(): void {
    const container = this.scheduleTable && this.scheduleTable.nativeElement;

    if (!this.shouldSetInitialScroll || !container) {
      return;
    }

    container.scrollLeft = this.getInitialTimelineScrollLeft(this.result);
    this.shouldSetInitialScroll = false;
  }

  getRoutes = (result) => {
    const resultData = this.getResultData(result);

    return (resultData && resultData.routes) || [];
  };

  getRouteOrders = (route) => {
    return (route && route.route) || [];
  };

  isRouteEmployeeOnTrip(route, result): boolean {
    // Снимок на момент расчёта — источник истины, в том числе при is_trip: false.
    const savedEmployee = ((this.getInputData(result) || {}).employees || []).find(
      (employee) => Number(employee && employee.id) === Number(route && route.employee_id)
    );
    if (savedEmployee && typeof savedEmployee.is_trip === 'boolean') {
      return savedEmployee.is_trip;
    }

    const source = this.getRouteEmployeeSources(route, result).find(
      (employee) => employee && employee.is_trip !== undefined && employee.is_trip !== null
    );

    return this.isTruthyMarker(source && source.is_trip);
  }

  isRouteEmployeeGph(route, result): boolean {
    return isGphFromSources(this.getRouteEmployeeSources(route, result));
  }

  getTimelineHours = (result) => {
    const bounds = this.getTimelineBounds(result);
    const hours = [];

    for (let hour = bounds.startHour; hour < bounds.endHour; hour++) {
      hours.push(hour);
    }

    return hours;
  };

  getVisibleRouteOrders = (route, result) => {
    return this.getRouteOrders(route).filter((order) => this.isOrderVisible(order, result));
  };

  getVisibleRouteOriginalOrderLayouts(route, result) {
    return this.getRoutePlannedOrdersLayout(route).items.filter((item) => this.isOrderOriginalTimeVisible(item.order, result));
  }

  getVisibleRouteTravels = (route, result) => {
    return this.getRouteTravels(route).filter((travel) => this.isTravelVisible(travel, result));
  };

  getRouteOrdersCount(route): number {
    return this.getRouteOrders(route).length;
  }

  getRouteDistance(route): string {
    return this.formatDistance(Number(route && route.total_travel_distance_meters) || 0);
  }

  getRouteEfficiencyPercent(route, result): string {
    let comparableOrdersCount = 0;
    const efficiencyMinutes = this.getRouteOrders(route).reduce((total, order) => {
      const plannedStart = this.getOrderOriginalStartValue(order);
      const plannedEnd = this.getOrderOriginalEndValue(order);
      const plannedWorkMinutes = this.getOrderPlannedWorkMinutes(order, result);

      if (!plannedStart || !plannedEnd || plannedWorkMinutes === null) {
        return total;
      }

      const plannedWindowMinutes = this.moment(plannedEnd).diff(this.moment(plannedStart), 'minutes');

      if (plannedWindowMinutes <= 0) {
        return total;
      }

      comparableOrdersCount++;

      return total + Math.max(0, plannedWindowMinutes - plannedWorkMinutes);
    }, 0);
    const shiftStart = route && (route.start_of_shift || route.route_start_time);
    const shiftEnd = route && (route.end_of_shift || route.route_end_time);

    if (!comparableOrdersCount || !shiftStart || !shiftEnd) {
      return '—';
    }

    const shiftStartTime = this.moment(shiftStart);
    let shiftEndTime = this.moment(shiftEnd);

    if (!shiftEndTime.isAfter(shiftStartTime)) {
      shiftEndTime = shiftEndTime.add(1, 'day');
    }

    const shiftMinutes = shiftEndTime.diff(shiftStartTime, 'minutes');

    if (shiftMinutes <= 0) {
      return '—';
    }

    const efficiencyPercent = Math.min(100, (efficiencyMinutes / shiftMinutes) * 100);

    return `${Math.round(efficiencyPercent)}%`;
  }

  getRouteRowHeight(route): number {
    if (!this.showFullTimeline) {
      return this.getRouteBaseHeight(route);
    }

    const laneCount = this.getOriginalAssignmentsLayout(route, this.result).laneCount;

    return this.getRouteBaseHeight(route) + 64 + Math.max(0, laneCount - 1) * 55;
  }

  getAdditionalTimelineTop(route): number {
    return this.getRouteBaseHeight(route);
  }

  getAdditionalTimelineHeight(route): number {
    const laneCount = this.getOriginalAssignmentsLayout(route, this.result).laneCount;

    return 64 + Math.max(0, laneCount - 1) * 55;
  }

  getOriginalAssignmentTop(route, lane: number): number {
    return this.getRouteBaseHeight(route) + 9 + lane * 55;
  }

  getPlannedOrderTop(lane: number): number {
    return 73 + lane * 55;
  }

  getVisibleRouteOrdersCount(route, result): number {
    return this.getVisibleRouteOrders(route, result).length;
  }

  getEmployeeWorkTime(route): string {
    return `${this.formatTime((route && route.start_of_shift) || (route && route.route_start_time))} - ${this.formatTime(
      (route && route.end_of_shift) || (route && route.route_end_time)
    )}`;
  }

  getRouteTransportIcon(route, result): 'car' | 'run' | 'bus' {
    const transportType = this.getRouteTransportType(route, result);

    if (transportType === 'driving') {
      return 'car';
    }

    if (transportType === 'public_transport') {
      return 'bus';
    }

    return 'run';
  }

  getRouteTransportTitle(route, result): string {
    const transportType = this.getRouteTransportType(route, result);

    if (transportType === 'driving') {
      return 'На машине';
    }

    if (transportType === 'public_transport') {
      return 'На общественном транспорте';
    }

    return 'Пешком';
  }

  getOrderLeftPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOrderVisibleRange(order, bounds);
    const minutesFromStart = Math.max(0, range.start.diff(this.getTimelineStart(range.start, bounds), 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.min(100, (minutesFromStart / totalMinutes) * 100);
  }

  getOrderWidthPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOrderVisibleRange(order, bounds);
    const durationMinutes = Math.max(10, range.end.diff(range.start, 'minutes'));
    const leftPercent = this.getOrderLeftPercent(order, result);
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.max(2, Math.min(100 - leftPercent, (durationMinutes / totalMinutes) * 100));
  }

  getOrderOriginalLeftPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOrderOriginalVisibleRange(order, bounds);
    const minutesFromStart = Math.max(0, range.start.diff(this.getTimelineStart(range.start, bounds), 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.min(100, (minutesFromStart / totalMinutes) * 100);
  }

  getOrderOriginalWidthPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOrderOriginalVisibleRange(order, bounds);
    const durationMinutes = Math.max(10, range.end.diff(range.start, 'minutes'));
    const leftPercent = this.getOrderOriginalLeftPercent(order, result);
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.max(2, Math.min(100 - leftPercent, (durationMinutes / totalMinutes) * 100));
  }

  getOriginalAssignmentLeftPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOriginalAssignmentVisibleRange(order, bounds);
    const minutesFromStart = Math.max(0, range.start.diff(this.getTimelineStart(range.start, bounds), 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.min(100, (minutesFromStart / totalMinutes) * 100);
  }

  getOriginalAssignmentWidthPercent(order, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getOriginalAssignmentVisibleRange(order, bounds);
    const durationMinutes = Math.max(10, range.end.diff(range.start, 'minutes'));
    const leftPercent = this.getOriginalAssignmentLeftPercent(order, result);
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.max(2, Math.min(100 - leftPercent, (durationMinutes / totalMinutes) * 100));
  }

  getTravelLeftPercent(travel, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getTravelVisibleRange(travel, bounds);
    const minutesFromStart = Math.max(0, range.start.diff(this.getTimelineStart(range.start, bounds), 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.min(100, (minutesFromStart / totalMinutes) * 100);
  }

  getTravelRightPercent(travel, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getTravelVisibleRange(travel, bounds);
    const minutesFromStart = Math.max(0, range.end.diff(this.getTimelineStart(range.end, bounds), 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);
    const endPercent = Math.min(100, (minutesFromStart / totalMinutes) * 100);

    return 100 - endPercent;
  }

  getTravelWidthPercent(travel, result): number {
    const bounds = this.getTimelineBounds(result);
    const range = this.getTravelVisibleRange(travel, bounds);
    const durationMinutes = Math.max(1, range.end.diff(range.start, 'minutes'));
    const leftPercent = this.getTravelLeftPercent(travel, result);
    const totalMinutes = this.getTimelineTotalMinutes(bounds);

    return Math.max(0.6, Math.min(100 - leftPercent, (durationMinutes / totalMinutes) * 100));
  }

  getTravelDisplayWidthPercent(travel, result, routeLine?: HTMLElement): number {
    const availableWidthPercent = this.getTravelAvailableWidthPercent(travel, result);

    if (this.isTravelCompact(travel, result, routeLine)) {
      return availableWidthPercent;
    }

    const travelWidthPercent = this.getTravelWidthPercent(travel, result);

    if (routeLine && routeLine.clientWidth) {
      const minimumTextWidthPercent = (100 / routeLine.clientWidth) * 100;

      return Math.min(availableWidthPercent, Math.max(travelWidthPercent, minimumTextWidthPercent));
    }

    return travelWidthPercent;
  }

  isTravelCompact(travel, result, routeLine?: HTMLElement): boolean {
    const availableWidthPercent = this.getTravelAvailableWidthPercent(travel, result);

    if (routeLine && routeLine.clientWidth) {
      return (routeLine.clientWidth * availableWidthPercent) / 100 < 100;
    }

    return availableWidthPercent < 10;
  }

  private getTravelAvailableWidthPercent(travel, result): number {
    const bounds = this.getTimelineBounds(result);
    const visibleRange = this.getTravelVisibleRange(travel, bounds);
    const availableStart = travel && travel.availableStart ? this.moment(travel.availableStart) : visibleRange.start;
    const timelineStart = this.getTimelineStart(visibleRange.end, bounds);
    const start = this.moment.max(availableStart, timelineStart);
    const durationMinutes = Math.max(1, visibleRange.end.diff(start, 'minutes'));
    const totalMinutes = this.getTimelineTotalMinutes(bounds);
    const rightPercent = this.getTravelRightPercent(travel, result);

    return Math.max(0.6, Math.min(100 - rightPercent, (durationMinutes / totalMinutes) * 100));
  }

  private getRouteBaseHeight(route): number {
    const laneCount = this.getRoutePlannedOrdersLayout(route).laneCount;

    return 128 + Math.max(0, laneCount - 1) * 55;
  }

  getTimelineRangeLabel(result): string {
    const bounds = this.getTimelineBounds(result);

    return `${this.formatHour(bounds.startHour)} - ${this.formatHour(bounds.endHour)}`;
  }

  canScrollTimelineBackward(container: HTMLElement): boolean {
    return !!container && container.scrollLeft > 1;
  }

  canScrollTimelineForward(container: HTMLElement): boolean {
    return !!container && container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
  }

  scrollTimeline(container: HTMLElement, direction: number): void {
    if (!container) {
      return;
    }

    const employeeColumnWidth = 225;
    const scrollStep = Math.max(this.timelineHourWidth * 3, container.clientWidth - employeeColumnWidth);
    const targetScrollLeft = container.scrollLeft + direction * scrollStep;

    container.scrollLeft = this.clamp(targetScrollLeft, 0, container.scrollWidth - container.clientWidth);
  }

  getTimelineWidth(result): number {
    const bounds = this.getTimelineBounds(result);
    const hoursCount = Math.max(this.visibleHoursCount, bounds.endHour - bounds.startHour);

    return hoursCount * this.timelineHourWidth;
  }

  private getInitialTimelineScrollLeft(result): number {
    const bounds = this.getTimelineBounds(result);
    const orderStarts = [];

    this.getRoutes(result).forEach((route) => {
      this.getRouteOrders(route).forEach((order) => {
        const start = order && (order.work_start_time || order.arrival_time || this.getOrderOriginalStartValue(order));

        if (start) {
          orderStarts.push(this.moment(start));
        }
      });
    });

    if (!orderStarts.length) {
      return 0;
    }

    const firstOrderStart = orderStarts.sort((a, b) => a.valueOf() - b.valueOf())[0];
    const timelineStart = firstOrderStart.clone().startOf('day').hour(bounds.startHour);
    const minutesBeforeFirstOrder = Math.max(0, firstOrderStart.diff(timelineStart, 'minutes') - 60);

    return (minutesBeforeFirstOrder / 60) * this.timelineHourWidth;
  }

  formatTime(value): string {
    if (!value) {
      return '';
    }

    return this.moment(value).format('HH:mm');
  }

  getOrderTimeRange(order): string {
    return `${this.formatTime(order && (order.work_start_time || order.arrival_time))} - ${this.formatTime(order && order.work_end_time)}`;
  }

  getOrderOriginalTimeRange(order): string {
    const start = this.getOrderOriginalStartValue(order);
    const end = this.getOrderOriginalEndValue(order);

    return start || end ? `${this.formatTime(start)} - ${this.formatTime(end)}` : '';
  }

  getOriginalAssignmentTimeRange(order): string {
    const start = this.getOriginalAssignmentStartValue(order);
    const end = this.getOriginalAssignmentEndValue(order);

    return start || end ? `${this.formatTime(start)} - ${this.formatTime(end)}` : '';
  }

  getOrderNumber(order): string {
    const ticket = this.getTicket(order);
    const value = (ticket && ticket.hd_ticket_number) || (order && (order.hd_ticket_number || order.order_id || order.id));

    return value ? String(value) : '-';
  }

  getOrderLink(order): string[] {
    const ticketId = order && (order.order_id || order.id);

    return ticketId ? ['/' + this.TICKETS_PATHS.ticketLink + '/' + ticketId] : null;
  }

  getTravelLabel(travel): string {
    return 'Движение';
  }

  getTravelTimeLabel(travel): string {
    return `${this.getTravelDurationMinutes(travel)} мин`;
  }

  getTravelTitle(travel): string {
    const distance = this.formatDistance(travel && travel.travelDistanceMeters);
    const fromOrder = travel && travel.fromOrder && travel.fromOrder.order_id ? travel.fromOrder.order_id : 'старт';
    const toOrder = travel && travel.toOrder && travel.toOrder.order_id;
    const arrivalTime = travel && travel.arrival ? `, прибытие ${this.formatTime(travel.arrival)}` : '';

    return `Движение ${fromOrder} → ${toOrder || ''}: ${this.getTravelDurationMinutes(travel)} мин${distance ? ', ' + distance : ''}${arrivalTime}`;
  }

  trackByRoute(index: number, route): string {
    return String((route && route.employee_id) || index);
  }

  trackByOrder(index: number, order): string {
    return String((order && order.order_id) || index);
  }

  trackByPlannedOrder(index: number, item): string {
    const order = item && item.order;

    return String((order && order.order_id) || index);
  }

  trackByOriginalAssignment(index: number, item): string {
    const order = item && item.order;

    return String((order && (order.order_id || order.id)) || index);
  }

  trackByTravel(index: number, travel): string {
    return (travel && travel.id) || String(index);
  }

  private makePlannedOrdersLayouts(routes): any {
    return (routes || []).reduce((layouts, route) => {
      const laneEnds = [];
      const items = this.getRouteOrders(route)
        .filter((order) => this.hasOrderOriginalTime(order))
        .slice()
        .sort((first, second) => this.getOrderOriginalStart(first).valueOf() - this.getOrderOriginalStart(second).valueOf())
        .map((order) => {
          const start = this.getOrderOriginalStart(order);
          const end = this.getOrderOriginalEnd(order);
          let lane = laneEnds.findIndex((laneEnd) => !start.isBefore(laneEnd));

          if (lane === -1) {
            lane = laneEnds.length;
          }

          laneEnds[lane] = end;

          return {
            lane: lane,
            order: order,
          };
        });

      layouts[this.getRouteLayoutKey(route)] = {
        items: items,
        laneCount: Math.max(1, laneEnds.length),
      };

      return layouts;
    }, {});
  }

  private getRoutePlannedOrdersLayout(route): any {
    return (
      this.plannedOrdersLayouts[this.getRouteLayoutKey(route)] || {
        items: [],
        laneCount: 1,
      }
    );
  }

  getOriginalAssignmentsLayout(route, result): any {
    const routeEmployeeId = this.toNumber(route && route.employee_id);
    const laneEnds = [];
    const items = this.getOriginalAssignmentOrders(result)
      .filter((order) => this.getOriginalAssignmentEmployeeId(order) === routeEmployeeId)
      .filter((order) => this.isOriginalAssignmentVisible(order, result))
      .slice()
      .sort((first, second) => this.getOriginalAssignmentStart(first).valueOf() - this.getOriginalAssignmentStart(second).valueOf())
      .map((order) => {
        const start = this.getOriginalAssignmentStart(order);
        const end = this.getOriginalAssignmentEnd(order);
        let lane = laneEnds.findIndex((laneEnd) => !start.isBefore(laneEnd));

        if (lane === -1) {
          lane = laneEnds.length;
        }

        laneEnds[lane] = end;

        return {
          lane: lane,
          order: order,
        };
      });

    return {
      items: items,
      laneCount: Math.max(1, laneEnds.length),
    };
  }

  private getRouteLayoutKey(route): string {
    return String(route && route.employee_id);
  }

  private getOrderPlannedWorkMinutes(order, result): number | null {
    const inputOrder = this.getInputOrder(order, result);
    const plannedWorkMinutes = Number(inputOrder && inputOrder.time_to_work);

    if (!isNaN(plannedWorkMinutes) && plannedWorkMinutes >= 0) {
      return plannedWorkMinutes;
    }

    if (!order || !order.work_start_time || !order.work_end_time) {
      return null;
    }

    return Math.max(0, this.moment(order.work_end_time).diff(this.moment(order.work_start_time), 'minutes'));
  }

  private getInputOrder(order, result) {
    const orderId = this.toNumber(order && (order.order_id || order.id));
    const input = this.getInputData(result);

    return ((input && input.orders) || []).find((item) => this.toNumber(item && item.id) === orderId) || null;
  }

  private getTimelineBounds(result): { startHour: number; endHour: number } {
    return this.getWorkdayBounds(result);
  }

  private getWorkdayBounds(result): { startHour: number; endHour: number } {
    const orderTimes = [];
    const shiftTimes = [];

    this.getRoutes(result).forEach((route) => {
      const shiftStart = (route && route.start_of_shift) || (route && route.route_start_time);
      const shiftEnd = (route && route.end_of_shift) || (route && route.route_end_time);

      if (shiftStart) {
        shiftTimes.push(this.moment(shiftStart));
      }
      if (shiftEnd) {
        shiftTimes.push(this.moment(shiftEnd));
      }

      this.getRouteOrders(route).forEach((order) => {
        if (order.work_start_time || order.arrival_time) {
          orderTimes.push(this.getOrderStart(order));
        }
        if (order.work_end_time) {
          orderTimes.push(this.getOrderEnd(order));
        }
        if (this.getOrderOriginalStartValue(order)) {
          orderTimes.push(this.getOrderOriginalStart(order));
        }
        if (this.getOrderOriginalEndValue(order)) {
          orderTimes.push(this.getOrderOriginalEnd(order));
        }
      });
    });

    const times = shiftTimes.length ? shiftTimes.concat(orderTimes) : orderTimes;

    if (!times.length) {
      return { startHour: 8, endHour: 24 };
    }

    const sorted = times.sort((a, b) => a.valueOf() - b.valueOf());
    const startHour = Math.max(0, sorted[0].hour());

    return {
      startHour: startHour,
      endHour: 24,
    };
  }

  private getOrderStart(order) {
    return this.moment(order.work_start_time || order.arrival_time);
  }

  private getOrderEnd(order) {
    return this.moment(order.work_end_time || order.work_start_time || order.arrival_time).add(!order.work_end_time ? 30 : 0, 'minutes');
  }

  private getOrderOriginalStart(order) {
    return this.moment(this.getOrderOriginalStartValue(order));
  }

  private getOrderOriginalEnd(order) {
    const end = this.getOrderOriginalEndValue(order);

    return end ? this.moment(end) : this.getOrderOriginalStart(order).add(30, 'minutes');
  }

  private getOriginalAssignmentStart(order) {
    return this.moment(this.getOriginalAssignmentStartValue(order));
  }

  private getOriginalAssignmentEnd(order) {
    const end = this.getOriginalAssignmentEndValue(order);

    return end ? this.moment(end) : this.getOriginalAssignmentStart(order).add(30, 'minutes');
  }

  private getRouteTravels(route) {
    const orders = this.getRouteOrders(route)
      .slice()
      .sort((a, b) => this.getOrderStart(a).valueOf() - this.getOrderStart(b).valueOf());
    const travels = [];

    orders.forEach((order, index) => {
      const previousOrder = index ? orders[index - 1] : null;
      const travel = this.makeTravelSegment(previousOrder, order, route);

      if (travel) {
        travels.push(travel);
      }
    });

    return travels;
  }

  private makeTravelSegment(previousOrder, order, route) {
    const arrival = order && order.arrival_time ? this.moment(order.arrival_time) : null;
    let end = order && (order.work_start_time || order.arrival_time) ? this.moment(order.work_start_time || order.arrival_time) : null;
    const routeStart = route && (route.route_start_time || route.start_of_shift);
    let start = previousOrder && previousOrder.work_end_time ? this.moment(previousOrder.work_end_time) : routeStart ? this.moment(routeStart) : null;
    const availableStart = start && start.clone();
    const travelMinutes = Number(order && order.travel_time_from_previous_minutes);
    const hasTravelMinutes = !isNaN(travelMinutes) && travelMinutes > 0;

    if (!hasTravelMinutes) {
      return null;
    }

    if (end) {
      start = end.clone().subtract(travelMinutes, 'minutes');
    } else if (start) {
      end = start.clone().add(travelMinutes, 'minutes');
    }

    if (!start || !end || !end.isAfter(start)) {
      return null;
    }

    return {
      id: `${(previousOrder && previousOrder.order_id) || 'start'}-${order && order.order_id}-${start.valueOf()}-${end.valueOf()}`,
      fromOrder: previousOrder,
      toOrder: order,
      availableStart: availableStart,
      start: start,
      end: end,
      arrival: arrival,
      travelTimeMinutes: travelMinutes,
      travelDistanceMeters: Number(order && order.travel_distance_from_previous_meters) || 0,
    };
  }

  private getOrderVisibleRange(order, bounds: { startHour: number; endHour: number }) {
    const orderStart = this.getOrderStart(order);
    const orderEnd = this.getOrderEnd(order);
    const timelineStart = this.getTimelineStart(orderStart, bounds);
    const timelineEnd = this.getTimelineEnd(orderStart, bounds);

    return {
      start: this.moment.max(orderStart, timelineStart),
      end: this.moment.min(orderEnd, timelineEnd),
    };
  }

  private getOrderOriginalVisibleRange(order, bounds: { startHour: number; endHour: number }) {
    const orderStart = this.getOrderOriginalStart(order);
    const orderEnd = this.getOrderOriginalEnd(order);
    const timelineStart = this.getTimelineStart(orderStart, bounds);
    const timelineEnd = this.getTimelineEnd(orderStart, bounds);

    return {
      start: this.moment.max(orderStart, timelineStart),
      end: this.moment.min(orderEnd, timelineEnd),
    };
  }

  private getOriginalAssignmentVisibleRange(order, bounds: { startHour: number; endHour: number }) {
    const orderStart = this.getOriginalAssignmentStart(order);
    const orderEnd = this.getOriginalAssignmentEnd(order);
    const timelineStart = this.getTimelineStart(orderStart, bounds);
    const timelineEnd = this.getTimelineEnd(orderStart, bounds);

    return {
      start: this.moment.max(orderStart, timelineStart),
      end: this.moment.min(orderEnd, timelineEnd),
    };
  }

  private getTravelVisibleRange(travel, bounds: { startHour: number; endHour: number }) {
    const timelineStart = this.getTimelineStart(travel.start, bounds);
    const timelineEnd = this.getTimelineEnd(travel.start, bounds);

    return {
      start: this.moment.max(travel.start, timelineStart),
      end: this.moment.min(travel.end, timelineEnd),
    };
  }

  private isOrderVisible(order, result): boolean {
    const bounds = this.getTimelineBounds(result);
    const orderStart = this.getOrderStart(order);
    const orderEnd = this.getOrderEnd(order);

    return orderStart.isBefore(this.getTimelineEnd(orderStart, bounds)) && orderEnd.isAfter(this.getTimelineStart(orderStart, bounds));
  }

  private isOrderOriginalTimeVisible(order, result): boolean {
    const bounds = this.getTimelineBounds(result);
    const orderStart = this.getOrderOriginalStart(order);
    const orderEnd = this.getOrderOriginalEnd(order);

    return orderStart.isBefore(this.getTimelineEnd(orderStart, bounds)) && orderEnd.isAfter(this.getTimelineStart(orderStart, bounds));
  }

  private isOriginalAssignmentVisible(order, result): boolean {
    const bounds = this.getTimelineBounds(result);
    const startValue = this.getOriginalAssignmentStartValue(order);

    if (!startValue) {
      return false;
    }

    const orderStart = this.getOriginalAssignmentStart(order);
    const orderEnd = this.getOriginalAssignmentEnd(order);

    return (
      orderStart.isBefore(this.getTimelineEnd(orderStart, bounds)) &&
      orderEnd.isAfter(this.getTimelineStart(orderStart, bounds))
    );
  }

  private isTravelVisible(travel, result): boolean {
    const bounds = this.getTimelineBounds(result);

    return travel.start.isBefore(this.getTimelineEnd(travel.start, bounds)) && travel.end.isAfter(this.getTimelineStart(travel.start, bounds));
  }

  private getTimelineTotalMinutes(bounds: { startHour: number; endHour: number }): number {
    return Math.max(60, (bounds.endHour - bounds.startHour) * 60);
  }

  private getTimelineStart(baseTime, bounds: { startHour: number; endHour: number }) {
    return baseTime.clone().startOf('day').hour(bounds.startHour).minute(0).second(0).millisecond(0);
  }

  private getTimelineEnd(baseTime, bounds: { startHour: number; endHour: number }) {
    return baseTime.clone().startOf('day').hour(bounds.endHour).minute(0).second(0).millisecond(0);
  }

  private formatHour(hour: number): string {
    return `${hour < 10 ? '0' : ''}${hour}:00`;
  }

  private getTravelDurationMinutes(travel): number {
    return Math.max(1, Math.round(Number((travel && travel.travelTimeMinutes) || 0)));
  }

  getOrderAddress(order): string {
    const ticket = this.getTicket(order) || this.getOriginalTicketByOrder(order);

    return (ticket && ticket.hd_address) || (order && (order.hd_address || order.address)) || '';
  }

  /**
   * Хвост подсказки с адресом. Отдельным методом, чтобы не дублировать
   * условие в трёх title подряд (гибкие слоты, текущие слоты, актуальный план)
   */
  getOrderAddressTitlePart(order): string {
    const address = this.getOrderAddress(order);

    return address ? `. Адрес: ${address}` : '';
  }

  /**
   * Блоки «Актуальный план» строятся из урезанного объекта makeOriginalAssignmentOrder,
   * адреса в нём нет — поэтому ищем исходную заявку по id
   */
  private getOriginalTicketByOrder(order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));

    if (orderId === null) {
      return null;
    }

    return this.getOriginalTicketsFlatList().find((ticket) => this.toNumber(ticket && ticket.id) === orderId) || null;
  }

  private getTicket(order) {
    const orderId = this.toNumber(order && (order.order_id || order.id));
    const tickets = this.tickets && this.tickets.dataFlatList;

    if (!orderId || !tickets) {
      return null;
    }

    return tickets.find((ticket) => this.toNumber(ticket && ticket.id) === orderId) || null;
  }

  private hasOrderOriginalTime(order): boolean {
    return !!this.getOrderOriginalStartValue(order);
  }

  private getOrderOriginalStartValue(order) {
    return order && (order.service_window_start || order.facility_start_time);
  }

  private getOrderOriginalEndValue(order) {
    return order && (order.service_window_end || order.facility_end_time);
  }

  private getOriginalAssignmentStartValue(order) {
    const ticket = this.getTicket(order);

    return (
      (order && (order.date_and_time_of_service_start || order.service_window_start || order.facility_start_time)) ||
      (ticket && ticket.hd_schedule_intbegin)
    );
  }

  private getOriginalAssignmentEndValue(order) {
    const ticket = this.getTicket(order);

    return (
      (order && (order.date_and_time_of_service_end || order.service_window_end || order.facility_end_time)) ||
      (ticket && ticket.hd_schedule_intend)
    );
  }

  private getOriginalAssignmentEmployeeId(order): number | null {
    return this.toNumber(order && (order.employee_id || order.fixed_employee_id));
  }

  private getOriginalAssignmentOrders(result): any[] {
    if (this.originalTickets === null || this.originalTickets === undefined) {
      return [];
    }

    return this.getOriginalTicketsFlatList()
      .map((ticket) => this.makeOriginalAssignmentOrder(ticket, result))
      .filter((order) => !!order);
  }

  private getOriginalTicketsFlatList(): any[] {
    const dates = Array.isArray(this.originalTickets)
      ? this.originalTickets
      : this.originalTickets && Array.isArray(this.originalTickets.data)
      ? this.originalTickets.data
      : [];
    const tickets = [];

    dates.forEach((date) =>
      ((date && date.intervals) || []).forEach((interval) =>
        ((interval && interval.districts) || []).forEach((district) =>
          ((district && district.tickets) || []).forEach((ticket) => tickets.push(ticket))
        )
      )
    );

    return tickets;
  }

  private makeOriginalAssignmentOrder(ticket, result) {
    const employeeId = this.getOriginalTicketEmployeeId(ticket, result);

    if (!ticket || employeeId === null) {
      return null;
    }

    return {
      id: ticket.id,
      name:
        (ticket.ticket_type && ticket.ticket_type.name) ||
        (ticket.hd_ticket_type && ticket.hd_ticket_type.name) ||
        'Заявка',
      employee_id: employeeId,
      date_and_time_of_service_start: ticket.hd_schedule_intbegin,
      date_and_time_of_service_end: ticket.hd_schedule_intend,
      hd_ticket_number: ticket.hd_ticket_number,
    };
  }

  private getOriginalTicketEmployeeId(ticket, result): number | null {
    const inputOrder = this.getInputOrder(ticket, result);
    const inputEmployeeId = this.toNumber(inputOrder && (inputOrder.employee_id || inputOrder.fixed_employee_id));

    if (this.hasRouteEmployee(inputEmployeeId, result)) {
      return inputEmployeeId;
    }

    const ticketBrigade = ticket && ticket.brigade;
    const brigadeId = this.toNumber(ticketBrigade && (ticketBrigade.id || ticketBrigade.brigade_id));
    const brigade = (this.brigades || []).find(
      (item) => this.toNumber(item && (item.id || item.brigade_id)) === brigadeId
    );
    const employeeId = this.getBrigadeEmployeeId(brigade || ticketBrigade);

    if (this.hasRouteEmployee(employeeId, result)) {
      return employeeId;
    }

    const employeeIdByName = this.getEmployeeIdByBrigadeName(ticketBrigade && ticketBrigade.brigade_name, result);

    if (employeeIdByName !== null) {
      return employeeIdByName;
    }

    return null;
  }

  private hasRouteEmployee(employeeId: number | null, result): boolean {
    return (
      employeeId !== null &&
      this.getRoutes(result).some((route) => this.toNumber(route && route.employee_id) === employeeId)
    );
  }

  private getBrigadeEmployeeId(brigade): number | null {
    if (!brigade) {
      return null;
    }

    const brigadeHead = brigade.brigade_head || brigade.head || brigade.employee;

    return this.toNumber(
      brigade.user_id ||
        brigade.employee_id ||
        brigade.e_id ||
        (brigadeHead && (brigadeHead.u_id || brigadeHead.user_id || brigadeHead.employee_id || brigadeHead.id))
    );
  }

  private getEmployeeIdByBrigadeName(brigadeName, result): number | null {
    const normalizedBrigadeName = this.normalizeEmployeeName(brigadeName).replace(/^бригада\s+/, '');

    if (!normalizedBrigadeName) {
      return null;
    }

    const route = this.getRoutes(result).find((item) => {
      const employeeName = this.normalizeEmployeeName(item && item.employee_name);

      return employeeName.indexOf(normalizedBrigadeName) === 0 || normalizedBrigadeName.indexOf(employeeName) === 0;
    });

    return route ? this.toNumber(route.employee_id) : null;
  }

  private normalizeEmployeeName(value): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private toNumber(value): number {
    const numberValue = Number(value);

    return isNaN(numberValue) ? null : numberValue;
  }

  private formatDistance(distanceMeters: number): string {
    if (!distanceMeters) {
      return '';
    }

    return distanceMeters >= 1000 ? `${(distanceMeters / 1000).toFixed(1)} км` : `${Math.round(distanceMeters)} м`;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private getRouteTransportType(route, result): string {
    const routeTransportType = this.normalizeTransportType(route && route.transport_type);

    if (routeTransportType) {
      return routeTransportType;
    }

    const employeeId = this.toNumber(route && route.employee_id);
    const input = this.getInputData(result);
    const employee = ((input && input.employees) || []).find(
      (item) => this.toNumber(item && (item.id || item.employee_id)) === employeeId
    );
    const employeeTransportType = this.normalizeTransportType(employee && employee.transport_type);

    if (employeeTransportType) {
      return employeeTransportType;
    }

    return this.hasEmployeeCar(route, result) ? 'driving' : 'walking';
  }

  private normalizeTransportType(value): string | null {
    const transportType = value === null || value === undefined ? '' : String(value).toLowerCase();

    return ['walking', 'driving', 'public_transport'].indexOf(transportType) > -1 ? transportType : null;
  }

  private hasEmployeeCar(route, result): boolean {
    const routeAvailability = this.getCarAvailability(route);

    if (routeAvailability !== null) {
      return routeAvailability;
    }

    const employeeId = this.toNumber(route && route.employee_id);
    const input = this.getInputData(result);
    const employee = ((input && input.employees) || []).find(
      (item) => this.toNumber(item && (item.id || item.employee_id)) === employeeId
    );

    return this.getCarAvailability(employee) === true;
  }

  private getCarAvailability(entity): boolean | null {
    if (!entity) {
      return null;
    }

    if (entity.availability_of_a_car !== undefined && entity.availability_of_a_car !== null) {
      return entity.availability_of_a_car === true;
    }

    const restriction = (entity.restrictions || []).find(
      (item) => item && item.availability_of_a_car !== undefined && item.availability_of_a_car !== null
    );

    return restriction ? restriction.availability_of_a_car === true : null;
  }

  private getResultData(result) {
    return result && result.results && typeof result.results === 'object'
      ? result.results
      : result && result.result && typeof result.result === 'object'
      ? result.result
      : result;
  }

  private getInputData(result) {
    const resultData = this.getResultData(result);

    return (
      (result && (result.input || result.inputs)) ||
      (resultData && (resultData.input || resultData.inputs)) ||
      null
    );
  }

  private getRouteEmployeeSources(route, result): any[] {
    const employeeId = this.toNumber(route && route.employee_id);
    const input = this.getInputData(result);

    return ((input && input.employees) || []).concat([route])
      .concat(this.employees || [])
      .filter((employee) => employee && (employee === route || this.getEmployeeId(employee) === employeeId));
  }

  private getEmployeeId(employee): number {
    return this.toNumber(employee && (employee.user_id || employee.employee_id || employee.id || employee.e_id));
  }

  private isTruthyMarker(value): boolean {
    return value === true || value === 1 || value === '1';
  }

}
